/* eslint-disable no-useless-constructor */
import Vue from 'vue';
import { DebounceSet } from './debounceSet';
import { PREFIX_CHAT_TRIGGERS } from './utils';
import { SystemMessageConfig } from './api/models.pleased';
import { FirebaseChatMessage } from '@/pleased-sdk/PleasedChat/PleasedChat.types';

const SYSTEM_TRIGGER_ADMIN = 1;
export class SystemTriggerMessageBroker {
  constructor(
    protected messageConfig: SystemMessageConfig[] = [],
    protected resumableChatSessionId: string = ''
  ) {}
  protected events = new Vue();
  protected executor = new DebounceSet();
  protected isDestroyed = false;
  protected sessionIdRecieved = false;
  protected checkLocalStorageOnlyOnce: Boolean = false;
  protected firstAutoMessageFromCustomer: Boolean = false;

  notifyNewMessage = (message: FirebaseChatMessage) => {
    // Do not run this trigger if
    // 1. This instance is destroyed.
    // 2. If the incoming message if from system trigger admin.
    // 3. If this message is a trigger message
    // 4. if it is sessionIdRecieved is equal to resumableChatSessionId(case on resuming)
    // 5. if it is a first message from the customer which is ideally a system message
    this.checkIfSomeDataExistsInLocalStorage(this.resumableChatSessionId);

    if (this.resumableChatSessionId && !this.sessionIdRecieved) {
      if (message.resumableChatSessionId === this.resumableChatSessionId) {
        this.sessionIdRecieved = true;
      }
      return;
    }
    if (
      this.isDestroyed ||
      (message.userId === SYSTEM_TRIGGER_ADMIN && !message.systemMessage) ||
      this.isTriggerMessage(message.message) ||
      message.userSelection ||
      message.isFromAI
    ) {
      return;
    }
    // New chat: skip only an initial customer bootstrap message when it is flagged as system.
    // Previously we returned on every message until a systemMessage arrived, so plain user text
    // never advanced the gate and time-since triggers never armed (resume worked because
    // resumableChatSessionId bypasses this block).
    if (!this.firstAutoMessageFromCustomer && !this.resumableChatSessionId) {
      this.firstAutoMessageFromCustomer = true;
      if (message.systemMessage) {
        return;
      }
    }
    // in case there are any more system msgs
    if (message.systemMessage && !this.resumableChatSessionId) {
      return;
    }

    const configType = message.from === 'agent' ? 'AGENT' : 'USER';
    this.messageConfig
      .filter((config) => configType === config.authorType)
      .forEach((config) => {
        // In current implementation, we are clearing all the triggers when a new message is received.
        // Which means that if any trigger was scheduled to run, it will be cleared and not run.
        this.executor.clearAll();
        if (config.messages && Array.isArray(config.messages)) {
          config.messages?.forEach((msg, index) => {
            const customKey = `${PREFIX_CHAT_TRIGGERS}${config.authorType}--${config.id}--${index}`;
            const delay = config.value * 1000 * (index + 1);
            const runAt = Date.now() + delay;
            const configData = {
              runAt,
              config,
            };
            localStorage.setItem(`${customKey}`, JSON.stringify(configData));

            this.executor.debounce(customKey, delay, () => this.emitMessage({ ...config, message: msg }));
          });
        } else if (config.message) {
          const customKey = `${PREFIX_CHAT_TRIGGERS}${config.authorType}--${config.id}`;
          const delay = config.value * 1000;
          const runAt = Date.now() + delay;
          const configData = {
            runAt,
            config,
          };
          localStorage.setItem(`${customKey}`, JSON.stringify(configData));
          this.executor.debounce(customKey, delay, () => this.emitMessage({ ...config, message: config.message }));
        }
      });
  };

  checkIfSomeDataExistsInLocalStorage(id: string) {
    if (!id || this.checkLocalStorageOnlyOnce) {
      return;
    }
    Object.entries(localStorage)
      .sort(([a], [b]) => {
        const aIndex = Number(a.split('--').pop());
        const bIndex = Number(b.split('--').pop());
        return aIndex - bIndex;
      })
      .map(([key, data]) => {
        if (!key.startsWith(PREFIX_CHAT_TRIGGERS) || !data) {
          return;
        }
        const value = JSON.parse(data);
        const { config, runAt } = value;
        const now = Date.now();

        const remaining = runAt - now;
        if (remaining <= 0) {
          localStorage.removeItem(key);
          return;
        }
        const callback = () => {
          let messageText = '';
          if (config.message) {
            messageText = config.message;
          } else {
            const index = key.split('--').pop() || 0;
            messageText = config.messages[+index];
          }
          this.emitMessage({ ...config, message: messageText });
        };
        this.executor.debounce(key, remaining, callback);
      });
    this.checkLocalStorageOnlyOnce = true;
  }

  /**
   * Register a function that will be called whenever a trigger
   * is matched.
   */
  onMatch(handler: SystemMessageHandler) {
    this.events.$on('SYSTEM_MESSAGE', handler);
  }

  destroy() {
    this.executor.clearAll();
    this.isDestroyed = true;
    this.events.$off('SYSTEM_MESSAGE');
  }

  protected emitMessage(config: SystemMessageConfig) {
    if (this.isDestroyed) return;
    this.events.$emit('SYSTEM_MESSAGE', { ...config });
  }

  protected isTriggerMessage(message: string) {
    return this.messageConfig.some((m) => m.message === message);
  }
}

type SystemMessageHandler = (message: SystemMessageConfig) => void;
